<script setup lang="ts">
import 'vue3-toastify/dist/index.css';
import { onMounted, reactive, ref } from 'vue';
import BaseInput from '/~/components/base/base-input.vue';
import BaseForm from '/~/components/base/base-form.vue';
import BaseButton from '/~/components/base/base-button.vue';
import BaseRadio from '/~/components/base/base-radio.vue';
import BaseCheckbox from '/~/components/base/base-checkbox.vue';
import EventsList from '/~/components/events-list.vue';
import { useOrder, configurePaymentElements, useCheckout } from '@eonx-com/payment-elements'
import { DemoEvent } from './types';
import BaseTextarea from '/~/components/base/base-textarea.vue';
import { toast } from 'vue3-toastify';

const { createOrder } = useOrder()

const processing = ref(false)
const events = ref<DemoEvent[]>([])

const formData = reactive({
  orderIntent: {
    apiKey: '',
    amount: '',
    reference: '',
    type: '',
    securityTokenRequired: true,
    jsonParams: '',
  },
  checkout: {
    apiKey: '',
    orderIntentId: '',
    experience: 'xpay',
    runtime: 'browser',
    deviceOS: '',

    attach: true,
    attachCard: {
      type: '',
      reference: '',
      amount: '',
    },
    saveToDraft: false,
    jsonParams: '',
  },
})

const checkout = ref()

async function onSubmit() {
  events.value = []
  checkout.value = undefined

  saveFormDataToStorage()

  const parsedParams = JSON.parse(formData.checkout.jsonParams || '{}')

  checkout.value = useCheckout(formData.checkout.experience, {
    container: '#checkout',
    orderIntentId: formData.checkout.orderIntentId,
    apiKey: formData.checkout.apiKey,
    test: true,
    runtime: formData.checkout.runtime,
    deviceOS: formData.checkout.deviceOS,
    attachCard: formData.checkout.attach ? formData.checkout.attachCard : undefined,
    ...parsedParams,
    onError(e: any) {
      events.value.push({ type: 'error', message: 'Checkout failed', details: e })
    },
    onSuccess(e: any) {
      events.value.push({ type: 'success', message: 'Successful checkout', details: JSON.stringify(e) })
    },
    onEvent(e: any) {
      events.value.push({ type: 'info', message: 'Event happened', details: JSON.stringify(e) })
    },
  });
}

async function createDummyOrderIntent() {
  events.value = []

  saveFormDataToStorage()

  const parsedParams = JSON.parse(formData.orderIntent.jsonParams || '{}')

  configurePaymentElements({
    apiKey: formData.orderIntent.apiKey,
    test: true,
  })

  const expiresAt = new Date()

  expiresAt.setMinutes(expiresAt.getMinutes() + 15)

  processing.value = true

  const [error, response] = await createOrder({
    destinations: [
      {
        amount: formData.orderIntent.amount,
        reference: formData.orderIntent.reference,
        type: formData.orderIntent.type as any,
      },
    ],
    securityTokenRequired: formData.orderIntent.securityTokenRequired,
    expiresAt: expiresAt.toISOString(),
    feeType: 'provider',
    ...parsedParams,
  })

  if (error) {
    toast(error.message || 'Order intent creation failed', { type: 'error' })
  } else {
    toast('Order intent created', { type: 'success' })
    formData.checkout.apiKey = response.apiKey.secret
    formData.checkout.orderIntentId = response.id
  }

  processing.value = false
}

onMounted(() => {
  loadFormDataFromStorage()

  configurePaymentElements({
    test: true,
  })
})

function saveFormDataToStorage() {
  localStorage.setItem('orderIntentDraft', JSON.stringify(formData.orderIntent))

  if(formData.checkout.saveToDraft) {
    localStorage.setItem('checkoutDraft', JSON.stringify(formData.checkout))
  } else {
    localStorage.removeItem('checkoutDraft')
  }
}

function loadFormDataFromStorage() {
  const orderIntentDraft = localStorage.getItem('orderIntentDraft')
  const checkoutDraft = localStorage.getItem('checkoutDraft')

  if (orderIntentDraft) {
    formData.orderIntent = JSON.parse(orderIntentDraft)
  }

  if (checkoutDraft) {
    formData.checkout = JSON.parse(checkoutDraft)
  }
}
</script>

<template>
  <div class="p-4 h-full md:h-screen md:overflow-y-auto">
    <div class="grid grid-cols-1 md:grid-cols-3 gap-4 h-full">
      <div class="rounded-lg bg-gray-200">
        <base-form class="grid grid-cols-1 gap-y-4 p-4 pb-0">
          <p class="text-xl font-bold">
            Order intent
          </p>

          <base-input
            v-model="formData.orderIntent.apiKey"
            name="intentApiKey"
            label="PG API Key"
            :disabled="processing"
            required
          />

          <div class="grid gap-y-4">
            <base-input
              v-model="formData.orderIntent.amount"
              name="amount"
              label="Amount (in cents)"
              required
              :disabled="processing"
            />

            <base-input
              v-model="formData.orderIntent.reference"
              name="reference"
              label="Reference"
              required
              :disabled="processing"
            />

            <base-input
              v-model="formData.orderIntent.type"
              name="type"
              label="Type"
              required
              :disabled="processing"
            />

            <base-checkbox
              v-if="formData.orderIntent.securityTokenRequired"
              v-model="formData.checkout.attach"
              :disabled="processing"
            >
              3DS Required
            </base-checkbox>
          </div>

          <base-textarea
            v-model="formData.orderIntent.jsonParams"
            :disabled="processing"
            label="Any json params"
            name="jsonParams"
            rules="json"
          />

          <base-button
            :disabled="processing"
            type="submit"
            class="mb-4"
            @click="createDummyOrderIntent"
          >
            Create dummy order intent
          </base-button>
        </base-form>
      </div>
      <div class="rounded-lg bg-gray-200 p-4">
        <p class="text-xl font-bold mb-4">
          SDK Checkout params
        </p>
        <base-form
          class="grid grid-cols-1 gap-y-4"
          @submit="onSubmit"
        >
          <base-input
            v-model="formData.checkout.apiKey"
            :disabled="processing"
            label="API Key"
            name="apiKey"
            required
          />
          <base-input
            v-model="formData.checkout.orderIntentId"
            :disabled="processing"
            label="Order Intent ID"
            required
            name="orderIntentId"
          />
          <div>
            <p>Experience</p>
            <div class="flex gap-x-4">
              <base-radio
                v-model="formData.checkout.experience"
                :disabled="processing"
                label="Default"
                value="default"
                name="experience"
              />
              <base-radio
                v-model="formData.checkout.experience"
                :disabled="processing"
                label="Xpay"
                value="xpay"
                name="experience"
              />
            </div>
          </div>

          <template v-if="formData.checkout.experience === 'xpay'">
            <div>
              <p>Runtime</p>
              <div class="flex gap-x-4">
                <base-radio
                  v-model="formData.checkout.runtime"
                  :disabled="processing"
                  label="Browser"
                  value="browser"
                  name="runtime"
                />
                <base-radio
                  v-model="formData.checkout.runtime"
                  :disabled="processing"
                  label="App"
                  value="app"
                  name="runtime"
                />
              </div>
            </div>

            <div v-if="formData.checkout.runtime === 'app'">
              <p>Platform</p>
              <div class="flex gap-x-4">
                <base-radio
                  v-model="formData.checkout.deviceOS"
                  :disabled="processing"
                  label="Android"
                  value="android"
                  name="platform"
                />
                <base-radio
                  v-model="formData.checkout.deviceOS"
                  :disabled="processing"
                  label="IOS"
                  value="ios"
                  name="platform"
                />
              </div>
            </div>
          </template>

          <base-textarea
            v-model="formData.checkout.jsonParams"
            :disabled="processing"
            label="Any json params"
            name="jsonParams"
            rules="json"
          />

          <template v-if="formData.checkout.experience !== 'xpay'">
            <base-checkbox
              v-model="formData.checkout.attach"
              :disabled="processing"
            >
              Attach source
            </base-checkbox>

            <template v-if="formData.checkout.attach">
              <base-input
                v-model="formData.checkout.attachCard.reference"
                :disabled="processing"
                label="Source reference"
                name="reference"
                required
              />

              <base-input
                v-model="formData.checkout.attachCard.amount"
                :disabled="processing"
                label="Source amount (in cents)"
                name="amount"
                required
              />

              <base-input
                v-model="formData.checkout.attachCard.type"
                :disabled="processing"
                label="Type"
                name="type"
                required
              />
            </template>
          </template>

          <base-checkbox
            v-model="formData.checkout.saveToDraft"
            :disabled="processing"
          >
            Save draft
          </base-checkbox>

          <base-button
            type="submit"
            :disabled="processing"
          >
            Go!
          </base-button>
        </base-form>
      </div>
      <div class="flex flex-col space-y-4">
        <div class="w-full rounded-lg bg-gray-200 p-4 flex justify-center h-full grow">
          <div
            id="checkout"
            class="w-full text-center border border-black p-4"
          >
            Checkout will be rendered here
          </div>
        </div>
        <events-list :events="events" class="flex flex-col grow md:max-h-96 h-full"/>
      </div>
    </div>
  </div>
</template>
